import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

const authHeader = (authorization: string) => ({
    "Content-Type": "application/json",
    Authorization: authorization,
})

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

// ─── Repediu ─────────────────────────────────────────────────────────────────

export interface IntegracaoRepediu {
    codigoRepediu: string | null
    chaveRepediu: string | null
}

export const obterRepediu = async function (
    authorization: string
): Promise<IntegracaoRepediu | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoRepediu>>("integracaoParceiros/ObterRepediu", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoRepediu>(response, "ObterRepediu")
    } catch (error) {
        RegistrarLogErro(error, "ObterRepediu", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarRepediu = async function (
    authorization: string,
    payload: IntegracaoRepediu
): Promise<IntegracaoRepediu | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<IntegracaoRepediu>>("integracaoParceiros/SalvarRepediu", payload, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoRepediu>(response, "SalvarRepediu")
    } catch (error) {
        RegistrarLogErro(error, "SalvarRepediu", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── DigiSac ─────────────────────────────────────────────────────────────────

export interface IntegracaoDigiSac {
    serviceId: string | null
    urlPlataforma: string | null
    tokenDigiSac: string | null
    ativo: boolean
}

export const obterDigiSac = async function (
    authorization: string
): Promise<IntegracaoDigiSac | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoDigiSac>>("integracaoParceiros/ObterDigiSac", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoDigiSac>(response, "ObterDigiSac")
    } catch (error) {
        RegistrarLogErro(error, "ObterDigiSac", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarDigiSac = async function (
    authorization: string,
    payload: IntegracaoDigiSac
): Promise<IntegracaoDigiSac | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<IntegracaoDigiSac>>("integracaoParceiros/SalvarDigiSac", payload, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoDigiSac>(response, "SalvarDigiSac")
    } catch (error) {
        RegistrarLogErro(error, "SalvarDigiSac", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export interface TesteDigiSac {
    mensagem: string
}

export const testarDigiSac = async function (
    authorization: string,
    telefone: string
): Promise<TesteDigiSac | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<TesteDigiSac>>(
                "integracaoParceiros/TestarDigiSac",
                { telefone },
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<TesteDigiSac>(response, "TestarDigiSac")
    } catch (error) {
        RegistrarLogErro(error, "TestarDigiSac", telefone)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── PagSeguro (somente leitura: token atual + URL de autorização) ───────────

export interface IntegracaoPagSeguro {
    tokenLoja: string | null
    urlAutorizacao: string
}

export const obterPagSeguro = async function (
    authorization: string
): Promise<IntegracaoPagSeguro | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoPagSeguro>>("integracaoParceiros/ObterPagSeguro", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoPagSeguro>(response, "ObterPagSeguro")
    } catch (error) {
        RegistrarLogErro(error, "ObterPagSeguro", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── Rede / e.Rede (cartão online) ───────────────────────────────────────────

export interface IntegracaoRede {
    pv: string | null
    token: string | null
    ambiente: string // "sandbox" | "producao"
    softDescriptor: string | null
    ativo: boolean
}

export const obterRede = async function (
    authorization: string
): Promise<IntegracaoRede | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoRede>>("integracaoParceiros/ObterErede", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoRede>(response, "ObterErede")
    } catch (error) {
        RegistrarLogErro(error, "ObterErede", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarRede = async function (
    authorization: string,
    payload: IntegracaoRede
): Promise<IntegracaoRede | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<IntegracaoRede>>("integracaoParceiros/SalvarErede", payload, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoRede>(response, "SalvarErede")
    } catch (error) {
        RegistrarLogErro(error, "SalvarErede", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── Uber ────────────────────────────────────────────────────────────────────

export interface IntegracaoUber {
    clienteId: string | null
    secretId: string | null
    costumerId: string | null
    observacaoRetirada: string | null
    minutosChamarEntregador: number | null
    retornarItemNaoEntregue: boolean
    automatico: boolean
    ativo: boolean
}

export const obterUber = async function (authorization: string): Promise<IntegracaoUber | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoUber>>("integracaoParceiros/ObterUber", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoUber>(response, "ObterUber")
    } catch (error) {
        RegistrarLogErro(error, "ObterUber", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarUber = async function (
    authorization: string,
    payload: IntegracaoUber
): Promise<IntegracaoUber | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<IntegracaoUber>>("integracaoParceiros/SalvarUber", payload, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoUber>(response, "SalvarUber")
    } catch (error) {
        RegistrarLogErro(error, "SalvarUber", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── Flux Delivery (logística parceira) ──────────────────────────────────────
// Config por loja: ativo, ambiente, tipo de serviço e CNPJ da Flux (o CNPJ identifica a loja na
// Flux). As credenciais OAuth2 são centralizadas na loja matriz e não trafegam aqui — o backend só
// sinaliza se já estão cadastradas (credenciaisPreenchidas).

export interface IntegracaoFluxDados {
    ambiente: string // "homologacao" | "producao"
    tipoServico: string // "RADIUS" | "TURBO60"
    cnpjFlux: string | null
    cnpjSugerido: string | null
    ativo: boolean
    credenciaisPreenchidas: boolean
    // Acionamento automático do entregador (padrão true). Quando false, o operador chama o
    // entregador manualmente pelo painel do pedido.
    acionamentoAutomatico: boolean
    // Minutos após o pedido antes de acionar o entregador (só no acionamento automático; 0 = imediato).
    tempoPreparoMinutos: number
}

export interface IntegracaoFluxPayload {
    ambiente: string
    tipoServico: string
    cnpjFlux: string | null
    ativo: boolean
    acionamentoAutomatico: boolean
    tempoPreparoMinutos: number
}

export interface ErroIntegracaoFluxItem {
    data: string
    operacao: string
    mensagem: string
    // Endpoint que falhou e corpo (mascarado) do request — diagnóstico. Podem vir ausentes.
    endpoint?: string | null
    corpo?: string | null
}

export interface TesteFlux {
    ativa: boolean
    mensagem: string
    erros: ErroIntegracaoFluxItem[]
}

// Resultado quando o próprio navegador cancelou/abortou a chamada de teste (ex.: outra chamada a
// substituiu, ou a tela foi trocada). Não é uma falha da Flux — a tela ignora sem alarmar.
export interface TesteFluxAbortado {
    abortado: true
}

// Distingue um ABORT (cancelamento) de uma falha real. Timeout NÃO entra aqui de propósito:
// um timeout é falha legítima e deve continuar sendo exibido.
function requisicaoFoiAbortada(error: unknown): boolean {
    const e = error as { code?: string; name?: string; message?: string }
    if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return true
    if (e?.code === "ECONNABORTED" && !/timeout/i.test(e?.message ?? "")) return true
    return e?.message === "Request aborted"
}

export const obterFlux = async function (
    authorization: string
): Promise<IntegracaoFluxDados | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoFluxDados>>("integracaoParceiros/ObterFlux", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoFluxDados>(response, "ObterFlux")
    } catch (error) {
        RegistrarLogErro(error, "ObterFlux", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarFlux = async function (
    authorization: string,
    payload: IntegracaoFluxPayload
): Promise<IntegracaoFluxDados | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<IntegracaoFluxDados>>("integracaoParceiros/SalvarFlux", payload, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoFluxDados>(response, "SalvarFlux")
    } catch (error) {
        RegistrarLogErro(error, "SalvarFlux", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const testarFlux = async function (
    authorization: string
): Promise<TesteFlux | ErroApi | TesteFluxAbortado> {
    try {
        const response = await api
            .post<RespostaRaw<TesteFlux>>(
                "integracaoParceiros/TestarFlux",
                {},
                { headers: authHeader(authorization) }
            )
            .catch(error =>
                requisicaoFoiAbortada(error)
                    ? { abortado: true as const }
                    : { data: erroParaRespostaRaw(error) }
            )
        if ("abortado" in response) {
            return response
        }
        return tratamentoResponse<TesteFlux>(response, "TestarFlux")
    } catch (error) {
        if (requisicaoFoiAbortada(error)) {
            return { abortado: true }
        }
        RegistrarLogErro(error, "TestarFlux", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── Pick'n Go (logística parceira) ──────────────────────────────────────────
// Config por loja: ativo + ambiente (homologacao/producao). As chaves da API ficam no backend
// (tabela PickngoCredencial, uma linha por ambiente); aqui só vem se cada ambiente tem credenciais.

export interface IntegracaoPickngoDados {
    ativo: boolean
    ambiente: "homologacao" | "producao"
    credenciaisHomologacao: boolean
    credenciaisProducao: boolean
}

export interface IntegracaoPickngoPayload {
    ativo: boolean
    ambiente: "homologacao" | "producao"
}

export interface TestePickngo {
    ativa: boolean
    mensagem: string
}

export const obterPickngo = async function (
    authorization: string
): Promise<IntegracaoPickngoDados | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoPickngoDados>>("integracaoParceiros/ObterPickngo", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoPickngoDados>(response, "ObterPickngo")
    } catch (error) {
        RegistrarLogErro(error, "ObterPickngo", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarPickngo = async function (
    authorization: string,
    payload: IntegracaoPickngoPayload
): Promise<IntegracaoPickngoDados | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<IntegracaoPickngoDados>>("integracaoParceiros/SalvarPickngo", payload, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoPickngoDados>(response, "SalvarPickngo")
    } catch (error) {
        RegistrarLogErro(error, "SalvarPickngo", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const testarPickngo = async function (authorization: string): Promise<TestePickngo | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<TestePickngo>>(
                "integracaoParceiros/TestarPickngo",
                {},
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<TestePickngo>(response, "TestarPickngo")
    } catch (error) {
        RegistrarLogErro(error, "TestarPickngo", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
